import {
  queryOptions,
  useMutation,
  useMutationState,
  useQueryClient,
  useSuspenseQuery,
  UseSuspenseQueryResult
} from '@tanstack/react-query';
import {useNavigate, useRouter, useRouterState} from "@tanstack/react-router";

import {useToast} from "@/hooks/use-toast";
import { TERMS_ACCEPT_FAILED } from '@/i18n';
import * as apiClient from '@/lib/api';
import { getToken, isTwoFactorInProgress, LocalStorageKeys } from "@/lib/token/token";
import { extractFirmsFromIdentity } from '@/lib/utils';
import { useToken } from '@/stores/token';
import Firm from '@/types/firm';
import { Identity } from '@/types/identity';
import { DataLoader } from '@/types/router-context';
import { Token } from '@/types/token';
import { QUERY_KEY_IDENTITY } from "@/utils/keys";

const identityQueryFn = () => {
  if (!localStorage.getItem('token') || isTwoFactorInProgress()) {
    return new Promise<any>(res => res(null))
  }

  return apiClient.get('/firm/profile')
}

export const identityQueryOptions = queryOptions({
  queryKey: [QUERY_KEY_IDENTITY],
  queryFn: identityQueryFn,
  throwOnError: true,
  retry: false,
  enabled: !isTwoFactorInProgress()
})

export const identityDataLoader = ({ context: { queryClient } }: DataLoader) => queryClient.ensureQueryData(identityQueryOptions)

export const useIdentity = (): UseSuspenseQueryResult<Identity | null> => {
  return useSuspenseQuery(identityQueryOptions)
}

type AuthSwitchResponse = {
  token: string
}

export const switchFirm = (firmId: number | null): Promise<AuthSwitchResponse> => apiClient.post('/auth/user/switch', {
  token: localStorage.getItem('token'),
  firm: firmId,
  group: null,
}, import.meta.env.VITE_USER_MANAGER_URL, false)

export const switchSubGroup = ({ subGroupId, identity }: { subGroupId: number, identity: Identity | null }): Promise<AuthSwitchResponse> => apiClient.post('/auth/user/switch', {
  token: localStorage.getItem('token'),
  firm: identity?.currentFirm?.id || null,
  group: subGroupId,
}, import.meta.env.VITE_USER_MANAGER_URL, false)

type AcceptTermsResponse = {
  token: string
}

/**
 * Records terms acceptance and returns a re-minted access token carrying the new claim.
 *
 * The token goes in the body and sendTokenInHeader is false, exactly like the switch calls above: a
 * vf-token header on a ^/auth/ route is intercepted by user-manager's 2FA authenticator, whose
 * claim whitelist a full access token does not fit.
 */
export const acceptTerms = (): Promise<AcceptTermsResponse> => apiClient.post('/auth/user/accept-terms', {
  token: getToken(),
}, import.meta.env.VITE_USER_MANAGER_URL, false)

export const useAcceptTerms = () => {
  const queryClient = useQueryClient()
  const router = useRouter()
  const navigate = useNavigate()
  const { toast } = useToast()

  return useMutation({
    mutationFn: acceptTerms,

    // Opts out of the app-wide mutation throwOnError (main.tsx), which escalates any error without
    // a statusCode — a fetch failure while user-manager is unreachable — to the router's error
    // boundary. That replaces the page with the generic "Something went wrong" component and takes
    // the Accept button with it, leaving a gated user stranded. The toast below is the whole of the
    // handling: the user must keep the retry in front of them.
    throwOnError: false,

    onSuccess: async ({ token }: AcceptTermsResponse) => {
      // Bare setItem rather than storeTokenData: accepting re-issues the access token only, and
      // storeTokenData clears the stored refresh token when it is not handed one. Roles do not
      // change on accept, so the cached roles stay correct.
      localStorage.setItem(LocalStorageKeys.TOKEN, token)

      const redirectTo = new URLSearchParams(location.search).get('redirect') || '/'

      await queryClient.invalidateQueries()
      // Re-runs the _auth guard against the new token, which now passes.
      await router.invalidate()
      await navigate({ to: redirectTo })
    },

    onError: (error: unknown) => {
      const description = error instanceof apiClient.ApiError && error.message
        ? error.message
        : TERMS_ACCEPT_FAILED

      toast({ description, variant: 'red' })
    },
  })
}

export const useFirmSelector = () => {
  const queryClient = useQueryClient()
  const { data: identity } = useIdentity()
  const { token } = useToken()
  const matches = useRouterState({ select: s => s.matches })
  const canSwitchFirm = matches[2].context.canSwitchFirm
  const {toast} = useToast()

  const { mutate: mutateFirm, variables: optimisticFirmId, isPending: isFirmSelectPending } = useMutation({
    mutationFn: (firmId: number | null): Promise<AuthSwitchResponse> => {
      queryClient.cancelQueries({
        predicate: query => !query.queryKey.includes(QUERY_KEY_IDENTITY)
      })

      return switchFirm(firmId)
    },

    onSuccess: ({ token } : AuthSwitchResponse) => {
      // A firm/subgroup switch re-issues the access token only. The refresh token identifies the
      // session, carries no firm context, and is left untouched (VFX-3042).
      localStorage.setItem('token', token)
    },

    onSettled: async () => {
      await queryClient.refetchQueries({ queryKey: [QUERY_KEY_IDENTITY] })
    },

    mutationKey: ['switch-firm']
  })

  const { mutate: mutateSubGroup, variables: optimisticSubGroupId, isPending: isSubGroupSelectPending } = useMutation({
    mutationFn: (subGroupId: number) => {
      return switchSubGroup({ subGroupId, identity })
    },

    onSuccess: ({ token } : AuthSwitchResponse) => {
      // A firm/subgroup switch re-issues the access token only. The refresh token identifies the
      // session, carries no firm context, and is left untouched (VFX-3042).
      localStorage.setItem('token', token)
    },

    onSettled: async () => {
      await queryClient.refetchQueries({ queryKey: [QUERY_KEY_IDENTITY] })
    }
  })

  if (identity === null) {
    return {
      selectFirm: () => {},
      selectSubGroup: () => {},
      optimisticFirmId: null,
      optimisticSubGroupId: undefined,
      isFirmSelectPending: false,
      isSubGroupSelectPending: false,
      selectedSubGroup: undefined,
      canSwitchFirm: false
    }
  }

  const { currentFirm } = identity
  const { subGroups } = extractFirmsFromIdentity(identity)
  const selectedSubGroup = getSelectedSubGroup(currentFirm, token, subGroups, isSubGroupSelectPending, optimisticSubGroupId)

  const handleSelectFirm = (firmId: number | null) => {
    if (!canSwitchFirm) {
      toast({ description: 'Firm switching is disabled on this page', variant: 'red' })
      return
    }

    mutateFirm(firmId)
  }

  const handleSelectSubgroup = (subGroupId: number) => {
    if (!canSwitchFirm) {
      toast({ description: 'Subgroup switching is disabled on this page', variant: 'red' })
      return
    }

    mutateSubGroup(subGroupId)
  }

  return {
    selectFirm: handleSelectFirm,
    selectSubGroup: handleSelectSubgroup,
    optimisticFirmId,
    optimisticSubGroupId,
    isFirmSelectPending,
    isSubGroupSelectPending,
    selectedSubGroup,
    canSwitchFirm
  }
}

/**
 * https://tanstack.com/query/latest/docs/framework/react/guides/optimistic-updates#if-the-mutation-and-the-query-dont-live-in-the-same-component
 */
export const useOptimisticFirmId = () : number | undefined => {
  const variables = useMutationState<number>({
    filters: { mutationKey: ['switch-firm'], status: 'pending' },
    select: (mutation) => mutation.state.variables as number,
  })

  if (variables.length < 1) {
    return undefined
  }

  return variables[0]
}

export function getSelectedSubGroup(currentFirm: Firm | null | undefined, token: Token | null, subGroups: Firm[], isSubGroupSelectPending: boolean, optimisticSubGroupId: number | undefined) {
  const availableSubgroupIds = (currentFirm !== null && currentFirm !== undefined && token?.firm_subgroups ? token.firm_subgroups[currentFirm.id] : []) || [];
  const availableSubgroups = subGroups.filter(sg => availableSubgroupIds.includes(sg.id));
  const selectedSubGroupId = isSubGroupSelectPending ? optimisticSubGroupId : token?.selected_subgroup;
  return availableSubgroups.find(sg => sg.id === selectedSubGroupId);
}
